import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { FaPlus, FaCheck, FaTimes, FaEdit, FaTrashAlt } from 'react-icons/fa';
import FaqForm from './FaqForm';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AddButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #27443a;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const StatusButton = styled.button`
  background: ${props => props.ativo ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

const ActionCell = styled.td`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #c0843d;
  font-size: 1.1rem;
  transition: background 0.2s;
  &:hover {
    background: #f8f8f8;
    color: #c62828;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: 38px;
  margin-top: 18px;
  color: #34574A;
`;

  const Faqs = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTooltip, setActiveTooltip] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [faqTipos, setFaqTipos] = useState([]);
    const [pageSize, setPageSize] = useState(20);
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [totalRegistos, setTotalRegistos] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [q, setQ] = useState('');
    const [pagina, setPagina] = useState(1);

  // Cores do sistema
  const PRIMARY = '#34574A'; // verde principal
  const PRIMARY_DARK = '#27443a';
  const BG_FAQ = '#f5f5f5';
  const CARD_BG = '#fff';
  const CARD_BG_ACTIVE = '#eaf2ef';
  const BORDER_ACTIVE = PRIMARY;
  const ICON_BG = '#e1eae6';
  const ICON_BG_ACTIVE = '#d0e0da';

  // Resumir respostas longas
  function resumirTexto(texto, limite = 220) {
    if (!texto) return '';
    const plain = texto.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    if (plain.length <= limite) return plain;
    return plain.slice(0, limite).replace(/[,;.]?\s+[^\s]*$/, '...');
  }

  // Tooltip para resposta longa
  const Tooltip = ({ text, children }) => (
    <span style={{ position: 'relative', cursor: 'pointer' }}
      onMouseEnter={e => setActiveTooltip(children)}
      onMouseLeave={() => setActiveTooltip(null)}>
      {children}
      {activeTooltip === children && (
        <span style={{ position: 'absolute', left: 0, top: 28, background: '#fff', color: '#222', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.11)', padding: 14, minWidth: 320, zIndex: 10, whiteSpace: 'pre-line' }}>
          {text}
        </span>
      )}
    </span>
  );

  const fetchFaqs = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Token não encontrado.');
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(pagina));
    params.set('pageSize', String(pageSize));
    if (q && q.trim()) params.set('q', q.trim());

    fetch(`${import.meta.env.VITE_URL_API}/faqs?${params.toString()}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        // Suporta tanto o novo formato { data, pagination } como o antigo [array]
        if (data && Array.isArray(data)) {
          setFaqs(data);
          setTotalRegistos(data.length);
          setTotalPaginas(Math.max(1, Math.ceil(data.length / pageSize)));
        } else if (data && data.data && data.pagination) {
          setFaqs(data.data);
          setTotalRegistos(data.pagination.total || 0);
          setTotalPaginas(data.pagination.totalPages || 1);
        } else {
          setFaqs([]);
          setTotalRegistos(0);
          setTotalPaginas(1);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Erro ao buscar FAQs.');
        setLoading(false);
      });
  };

  // Buscar FAQs quando pagina, pageSize ou q mudarem
  useEffect(() => {
    fetchFaqs();
  }, [pagina, pageSize, q]);

  // Buscar tipos de FAQ apenas uma vez
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetch(`${import.meta.env.VITE_URL_API}/faqtipos`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => res.json())
        .then(data => setFaqTipos(data));
    }
  }, []);

  // Função para adicionar ou editar FAQ
  const handleFormSubmit = async (formData) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const body = JSON.stringify({
        tipoFAQ: formData.tipoFAQ,
        perguntaFAQ: formData.perguntaFAQ,
        respostaFAQ: formData.respostaFAQ,
        ativoFAQ: formData.ativoFAQ
      });
      if (editData) {
        await fetch(`${import.meta.env.VITE_URL_API}/faqs/${editData.codigoFAQ}`, {
          method: 'PATCH',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body
        });
        Swal.fire('Sucesso', 'FAQ atualizado com sucesso!', 'success');
      } else {
        await fetch(`${import.meta.env.VITE_URL_API}/faqs`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body
        });
        Swal.fire('Sucesso', 'FAQ criado com sucesso!', 'success');
      }
      setShowForm(false);
      setEditData(null);
      fetchFaqs();
    } catch {
      Swal.fire('Erro', 'Não foi possível salvar o FAQ.', 'error');
    }
  };

  const handleAdd = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handleEdit = (faq) => {
    setEditData({
      codigoFAQ: faq.codigoFAQ,
      tipoFAQ: faq.tipoFAQ,
      perguntaFAQ: faq.perguntaFAQ,
      respostaFAQ: faq.respostaFAQ,
      ativoFAQ: faq.ativoFAQ
    });
    setShowForm(true);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditData(null);
  };

  const handleToggleAtivo = async (faq) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const novoStatus = faq.ativoFAQ === 1 ? 0 : 1;
      await fetch(`${import.meta.env.VITE_URL_API}/faqs/${faq.codigoFAQ}`, {
        method: 'PATCH',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tipoFAQ: faq.tipoFAQ,
          perguntaFAQ: faq.perguntaFAQ,
          respostaFAQ: faq.respostaFAQ,
          ativoFAQ: novoStatus
        })
      });
      setTimeout(fetchFaqs, 400);
    } catch {
      Swal.fire('Erro', 'Não foi possível atualizar o status.', 'error');
    }
  };

  // Função para deletar FAQ
  const handleDelete = (faq) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: `Deseja realmente excluir a FAQ "${faq.perguntaFAQ}"? Esta ação não poderá ser desfeita!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/faqs/${faq.codigoFAQ}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          setTimeout(fetchFaqs, 400);
          Swal.fire('Sucesso', 'FAQ excluída com sucesso!', 'success');
        } catch {
          Swal.fire('Erro', 'Não foi possível excluir a FAQ.', 'error');
        }
      }
    });
  };

  return (
    <PageContainer style={{ background: BG_FAQ }}> 
      {loading && <div>Carregando...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!loading && !error && (
        <>
          <HeaderRow>
            <h1 style={{margin:0}}>F.A.Q - Perguntas frequentes</h1>
            <div style={{display:'flex', gap:12, alignItems:'center'}}>
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchInput}
                onChange={(e)=>setSearchInput(e.target.value)}
                onKeyDown={(e)=>{ if(e.key==='Enter'){ setPagina(1); setQ(searchInput); } }}
                style={{padding:'10px 12px', border:'1px solid #ddd', borderRadius:8}}
              />
              <button
                onClick={()=>{ setPagina(1); setQ(searchInput); }}
                style={{padding:'10px 14px', border:'1px solid #ddd', background:'#fff', borderRadius:8, cursor:'pointer'}}
              >Buscar</button>
              <select
                value={pageSize}
                onChange={(e)=>{ setPagina(1); setPageSize(Number(e.target.value)); }}
                style={{padding:'10px 12px', border:'1px solid #ddd', borderRadius:8}}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <AddButton onClick={handleAdd}><FaPlus /> Novo FAQ</AddButton>
            </div>
          </HeaderRow>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tipo</th>
                  <th>Pergunta</th>
                  <th>Resposta</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map(faq => (
                  <tr key={faq.codigoFAQ}>
                    <td>{faq.codigoFAQ}</td>
                    <td>{faq.faqTipo?.nomeFAQTIPO || '-'}</td>
                    <td>{faq.perguntaFAQ}</td>
                    <td>
                      <Tooltip text={faq.respostaFAQ}>
                        {resumirTexto(faq.respostaFAQ, 80)}
                      </Tooltip>
                    </td>
                    <td>
                      <StatusButton
                        ativo={faq.ativoFAQ === 1}
                        onClick={() => handleToggleAtivo(faq)}
                        title={faq.ativoFAQ === 1 ? 'Desativar' : 'Ativar'}
                      >
                        {faq.ativoFAQ === 1 ? <FaCheck /> : <FaTimes />} {faq.ativoFAQ === 1 ? 'Ativo' : 'Inativo'}
                      </StatusButton>
                    </td>
                    <ActionCell>
                      <IconButton title="Editar" onClick={()=>handleEdit(faq)}><FaEdit /></IconButton>
                      <IconButton title="Excluir" onClick={()=>handleDelete(faq)}><FaTrashAlt /></IconButton>
                    </ActionCell>
                  </tr>
                ))}
              </tbody>
            </Table>
            {/* Paginação */}
            <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,margin:'24px 0'}}>
              <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={pagina===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===1?'not-allowed':'pointer'}}>Anterior</button>
              {[...Array(totalPaginas)].map((_,i)=>(
                <button
                  key={i}
                  onClick={()=>setPagina(i+1)}
                  style={{padding:'6px 12px',borderRadius:6,border:'1.5px solid '+(pagina===i+1?'#34574A':'#eee'),background:pagina===i+1?'#34574A':'#fff',color:pagina===i+1?'#fff':'#222',fontWeight:600,cursor:'pointer'}}
                >{i+1}</button>
              ))}
              <button onClick={()=>setPagina(p=>Math.min(totalPaginas,p+1))} disabled={pagina===totalPaginas} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===totalPaginas?'not-allowed':'pointer'}}>Próxima</button>
            </div>
            <div style={{display:'flex', justifyContent:'center', color:'#666', marginBottom: 16}}>
              <span>Total: {totalRegistos}</span>
            </div>
            {showForm && (
              <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.25)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <div style={{background:'#fff',padding:32,borderRadius:12,minWidth:360}}>
                  <h2 style={{marginBottom:16}}>{editData ? 'Editar FAQ' : 'Novo FAQ'}</h2>
                  <FaqForm onSubmit={handleFormSubmit} onCancel={handleFormCancel} initialData={editData} tipos={faqTipos} />
                </div>
              </div>
            )}
          </TableWrapper>
        </>
      )}
    </PageContainer>
  );
};

export default Faqs;
